import { useUpdateUserMarketingMutation } from "@entities/user/api/useUpdateUserMarketingMutation.ts";
import { useUserIdFromSdk } from "@entities/user/model/userStore.ts";
import {
	FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType,
	FxIdWebFeaturesProfileGetResponse
} from "@fxid/openapi-client";
import { queries } from "@fxid/openapi-client";
import {
	ActionIcon,
	Avatar,
	Badge,
	Box,
	Card,
	Divider,
	Grid,
	Group,
	Stack,
	Switch,
	Text,
	Title,
	Tooltip
} from "@mantine/core";
import { ConnectedGamesSection } from "@pages/ProfilePage/ConnectedGamesSection.tsx";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import {
	FaApple,
	FaCheck,
	FaCrown,
	FaEnvelope,
	FaFacebook,
	FaGamepad,
	FaGoogle,
	FaRegCopy,
	FaSteam,
	FaTelegram,
	FaTwitch,
	FaVk,
	FaYandex
} from "react-icons/fa";
import { FaX } from "react-icons/fa6";
import { SiEpicgames, SiFirebase } from "react-icons/si";

import classes from "./Profile.module.scss";

export const UserProfileCard = ({ profile }: { profile: FxIdWebFeaturesProfileGetResponse }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [copied, setCopied] = React.useState(false);
	const [idCopied, setIdCopied] = React.useState(false);
	const [copiedSocialId, setCopiedSocialId] = React.useState<string | null>(null);

	const { mutate: updateSettings, isPending: isUpdating } = useUpdateUserMarketingMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queries.profile._def });
		}
	});

	const email = profile.Email ?? "";
	const userId = profile.AccountId ?? undefined;

	// Try common flags that may indicate verification; safe cast avoids TS errors if the field isn't present
	const isEmailValidated = profile.EmailValidated;

	// GeoCountry and Locale to be shown as badges
	const geoCountry = profile.GeoCountry ?? "";
	const locale = (profile as any).Locale ?? (typeof navigator !== "undefined" ? navigator.language : "") ?? "";

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(email);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			// no-op; clipboard might be blocked
		}
	};

	const handleCopyUserId = async () => {
		if (!userId) return;
		try {
			await navigator.clipboard.writeText(userId.toString());
			setIdCopied(true);
			window.setTimeout(() => setIdCopied(false), 1500);
		} catch {
			// no-op; clipboard might be blocked
		}
	};

	const handleCopySocialId = async (value: string) => {
		try {
			await navigator.clipboard.writeText(value);
			setCopiedSocialId(value);
			window.setTimeout(() => setCopiedSocialId(null), 1500);
		} catch {
			// no-op
		}
	};

	type SocialType = FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType;

	const socialMeta: Partial<Record<SocialType, { label: string; color: string; icon: React.ReactNode }>> = {
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Telegram]: {
			label: "Telegram",
			color: "cyan",
			icon: <FaTelegram />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Firebase]: {
			label: "Firebase",
			color: "orange",
			icon: <SiFirebase />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.VkontaktePlay]: {
			label: "VK Play",
			color: "blue",
			icon: <FaVk />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Odnoklassniki]: {
			label: "Odnoklassniki",
			color: "orange",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.VkontakteCom]: {
			label: "VK",
			color: "blue",
			icon: <FaVk />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.YandexGames]: {
			label: "Yandex Games",
			color: "yellow",
			icon: <FaYandex />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.FxIdGuest]: {
			label: "Guest",
			color: "gray",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.YandexGamesGuest]: {
			label: "Yandex Games (Guest)",
			color: "yellow",
			icon: <FaYandex />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Facebook]: {
			label: "Facebook",
			color: "blue",
			icon: <FaFacebook />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Google]: {
			label: "Google",
			color: "red",
			icon: <FaGoogle />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Apple]: {
			label: "Apple",
			color: "dark",
			icon: <FaApple />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.AbsoluteGamesGuest]: {
			label: "Absolute Games (Guest)",
			color: "gray",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.AbsoluteGames]: {
			label: "Absolute Games",
			color: "grape",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.KongregateGuest]: {
			label: "Kongregate (Guest)",
			color: "gray",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Kongregate]: {
			label: "Kongregate",
			color: "red",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.VkontakteComOauth]: {
			label: "VK (OAuth)",
			color: "blue",
			icon: <FaVk />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Xsolla]: {
			label: "Xsolla",
			color: "pink",
			icon: <FaX />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.FacebookGames]: {
			label: "Facebook Games",
			color: "blue",
			icon: <FaFacebook />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.FacebookInstantGamesPlayerId]: {
			label: "Facebook Instant Games",
			color: "blue",
			icon: <FaFacebook />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.CrazyGamesGuest]: {
			label: "CrazyGames (Guest)",
			color: "gray",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.CrazyGames]: {
			label: "CrazyGames",
			color: "teal",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.MyGamesId]: {
			label: "MY.GAMES",
			color: "violet",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.FacebookNamespaced]: {
			label: "Facebook (Namespaced)",
			color: "blue",
			icon: <FaFacebook />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.WizQ]: {
			label: "WizQ",
			color: "indigo",
			icon: <FaGamepad />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Steam]: {
			label: "Steam",
			color: "dark",
			icon: <FaSteam />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Egs]: {
			label: "Epic Games",
			color: "dark",
			icon: <SiEpicgames />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Twitch]: {
			label: "Twitch",
			color: "grape",
			icon: <FaTwitch />
		},
		[FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.AW]: {
			label: "AW",
			color: "gray",
			icon: <FaGamepad />
		}
	};

	const resolveSocialMeta = (type: SocialType) => {
		if (socialMeta[type]) return socialMeta[type]!;
		if (type === FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Email) {
			return { label: t("screen_profile.email_address", "Email"), color: "gray", icon: <FaEnvelope /> };
		}
		return { label: "Unknown", color: "gray", icon: <FaGamepad /> };
	};

	const socialIdentifiers = (profile.SocialIdentifiers ?? []).filter(
		(s) => s.Type !== FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.Email
	);

	return (
		<Card radius={"lg"} withBorder className={classes.glassCard}>
			<Card.Section
				inheritPadding
				p={"lg"}
				style={{
					background: "linear-gradient(90deg, rgba(99,102,241,0.20) 0%, rgba(14,165,233,0.14) 100%)",
					borderBottom: "1px solid var(--mantine-color-dark-5)"
				}}
			>
				<Group justify="space-between" align="center">
					<Group>
						<Box
							style={{
								padding: 3,
								borderRadius: 999,
								background: "linear-gradient(135deg, rgba(129,140,248,0.8), rgba(56,189,248,0.75))"
							}}
						>
							<Avatar
								color="indigo"
								radius="xl"
								size={64}
								className={classes.profileAvatar}
								styles={{
									placeholder: { background: "rgba(0,0,0,0.35)" }
								}}
							>
								<FaCrown size="1.6rem" />
							</Avatar>
						</Box>

						<Stack gap={2}>
							<Title order={3} size="h4">
								{t("profile", "Profile")}
							</Title>
							<Group gap="xs">
								<Badge size="sm" variant="light" color="indigo" radius="sm">
									{t("account_status", "Account")}
								</Badge>
								{profile.MarketingEnabled && (
									<Badge size="sm" variant="light" color="teal" radius="sm">
										{t("subscribed", "Subscribed")}
									</Badge>
								)}
								{geoCountry && (
									<Badge
										size="sm"
										variant="light"
										color="grape"
										radius="sm"
										title={t("screen_profile.geo_country", "Geo country")}
									>
										GEO: {geoCountry}
									</Badge>
								)}
								{locale && (
									<Badge
										size="sm"
										variant="light"
										color="cyan"
										radius="sm"
										title={t("screen_profile.locale", "Locale")}
									>
										{locale}
									</Badge>
								)}
							</Group>
						</Stack>
					</Group>
				</Group>
			</Card.Section>
			<Stack>
				<Grid mt="lg">
					<Grid.Col span={6}>
						<Text size="sm" c="dimmed" fw={500}>
							{t("screen_profile.email_address", "Email")}
						</Text>

						{email ? (
							<Group gap="xs" mt={6} wrap="nowrap">
								<FaEnvelope className={classes.icon} aria-hidden="true" />
								<Text style={{ wordBreak: "break-all" }}>{email}</Text>

								{isEmailValidated && (
									<Tooltip label={t("email_verified", "Email verified")}>
										<Badge
											size="sm"
											variant="light"
											color="green"
											radius="sm"
											leftSection={<FaCheck size="0.65rem" />}
										>
											{t("verified", "Verified")}
										</Badge>
									</Tooltip>
								)}

								<Tooltip label={copied ? t("copied", "Copied!") : t("copy", "Copy")}>
									<ActionIcon
										variant="subtle"
										color={copied ? "teal" : "gray"}
										aria-label={t("copy_email", "Copy email")}
										onClick={handleCopy}
									>
										{copied ? <FaCheck /> : <FaRegCopy />}
									</ActionIcon>
								</Tooltip>
							</Group>
						) : (
							<Text mt={6}>{t("screen_profile.no_email", "No email added")}</Text>
						)}
					</Grid.Col>

					<Grid.Col span={6}>
						<Text size="sm" c="dimmed" fw={500}>
							{t("screen_profile.user_id", "User ID")}
						</Text>

						{userId ? (
							<Group gap="xs" mt={6} wrap="nowrap">
								<Text style={{ wordBreak: "break-all" }}>{userId}</Text>
								<Tooltip label={idCopied ? t("copied", "Copied!") : t("copy", "Copy")}>
									<ActionIcon
										variant="subtle"
										color={idCopied ? "teal" : "gray"}
										aria-label={t("copy_user_id", "Copy user id")}
										onClick={handleCopyUserId}
									>
										{idCopied ? <FaCheck /> : <FaRegCopy />}
									</ActionIcon>
								</Tooltip>
							</Group>
						) : (
							<Text mt={6}>{t("screen_profile.no_user_id", "No user ID")}</Text>
						)}
					</Grid.Col>
				</Grid>

				<Divider opacity={0.35} />

				<div>
					<Title order={4} mt="xs" mb="sm">
						{t("screen_profile.social_accounts", "Social accounts")}
					</Title>

					{socialIdentifiers.length > 0 ? (
						<Stack gap="sm">
							{socialIdentifiers.map((s) => {
								const meta = resolveSocialMeta(s.Type);
								const isCopied = copiedSocialId === s.Id;
								return (
									<Box
										key={`${s.Type}-${s.Id}`}
										className={classes.preferenceItem}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: "var(--mantine-spacing-md)",
											padding: "var(--mantine-spacing-sm)",
											borderRadius: "var(--mantine-radius-md)",
											backgroundColor: "rgba(255,255,255,0.02)",
											border: "1px solid var(--mantine-color-dark-5)"
										}}
									>
										<Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
											<Badge
												size="sm"
												variant="light"
												color={meta.color as any}
												radius="sm"
												leftSection={meta.icon}
												title={meta.label}
											>
												{meta.label}
											</Badge>
											<Text style={{ wordBreak: "break-all" }}>{s.Id}</Text>
										</Group>

										<Tooltip label={isCopied ? t("copied", "Copied!") : t("copy", "Copy")}>
											<ActionIcon
												variant="subtle"
												color={isCopied ? "teal" : "gray"}
												aria-label={t("copy_social_identifier", "Copy social identifier")}
												onClick={() => handleCopySocialId(s.Id)}
											>
												{isCopied ? <FaCheck /> : <FaRegCopy />}
											</ActionIcon>
										</Tooltip>
									</Box>
								);
							})}
						</Stack>
					) : (
						<Text c="dimmed">
							{t("screen_profile.no_social_accounts", "No social identifiers connected")}
						</Text>
					)}
				</div>

				<div>
					<Title order={4} mt="xs" mb="sm">
						{t("screen_profile.preferences", "Preferences")}
					</Title>

					<Box
						className={classes.preferenceItem}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "var(--mantine-spacing-md)",
							padding: "var(--mantine-spacing-md)",
							borderRadius: "var(--mantine-radius-md)",
							backgroundColor: "rgba(255,255,255,0.02)",
							border: "1px solid var(--mantine-color-dark-5)"
						}}
					>
						<Switch
							aria-label={t("marketing_button", "Marketing email")}
							classNames={{ track: classes.switchTrack }}
							checked={profile.MarketingEnabled}
							onChange={(event) => {
								updateSettings({ MarketingEnabled: event.currentTarget.checked });
							}}
							disabled={isUpdating}
							label={t("screen_profile.marketing_emails", "Marketing email")}
							description={t(
								"screen_profile.marketing_emails_description",
								"Receive occasional emails about updates"
							)}
							size="md"
							onLabel="ON"
							offLabel="OFF"
						/>
					</Box>
				</div>
				<div>
					<Title order={4}  mt="xs" mb="sm">{t("screen_profile.your_games")}</Title>
					<ConnectedGamesSection profile={profile}></ConnectedGamesSection>
				</div>
			</Stack>
		</Card>
	);
};
